import { AddIcon } from '@chakra-ui/icons';
import { Badge, Box, Flex, Heading, IconButton, useDisclosure } from '@chakra-ui/react';
import { ColumnDef } from '@tanstack/react-table';
import ReactTable from 'components/ReactTable';
import StateText from 'components/StateText';
import { CONNECTION_STATUS } from 'helpers/constant';
import useQueryParam from 'hooks/useQueryParam';
import useConnection, { SharepointConnection, useConfirmConnection } from 'queries/useConnection';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';

import AddConnectionModal from './AddConnectionModal';
import ConnectionActionButtons from './ConnectionActionButtons';

const SyncSharepoint = (): JSX.Element => {
  const query = useQueryParam();
  const { t } = useTranslation();
  const connectionId = query.get('connection_id');
  const {
    data: connections = [],
    isFetching,
    isFetched,
  } = useConnection({ enabled: !connectionId });

  const history = useHistory();
  const { pathname } = useLocation();

  const { mutate: confirmConnection, isPending: isConfirming } = useConfirmConnection();

  const { isOpen: isCreateOpen, onOpen: onCreateOpen, onClose: onCreateClose } = useDisclosure();

  useEffect(() => {
    if (connectionId) {
      confirmConnection(connectionId, {
        onSettled: () => {
          history.replace(pathname);
        },
      });
    }
  }, [connectionId, confirmConnection, pathname, history]);

  const columns: ColumnDef<SharepointConnection>[] = [
    {
      header: t('connection.name'),
      accessorKey: 'display_name',
    },
    {
      header: t('common.status'),
      accessorKey: 'status_code',
      cell: (info) => {
        return (
          <Badge
            px={2}
            py={0.2}
            borderRadius='md'
            colorScheme={
              info.getValue() === 'Error' || info.getValue() === CONNECTION_STATUS.UNAUTHENTICATED
                ? 'yellow'
                : 'green'
            }
          >
            <StateText statusKey={info.getValue() as string} />
          </Badge>
        );
      },
    },
    {
      header: t('connection.authenticatedUser'),
      accessorKey: 'authenticated_user',
      cell: (info) => info.getValue() || 'N/A',
    },
    {
      header: t('common.actions'),
      accessorKey: 'id',
      cell: ({ row: { original } }) => <ConnectionActionButtons info={original} />,
    },
  ];

  return (
    <Flex direction='column' px={6}>
      <Flex align='center' justify='space-between' mb={4}>
        <Heading as='h3' mr='1rem' fontWeight={'bold'} size='md'>
          {t('connection.sharePointConnection')}
        </Heading>
        <IconButton
          aria-label={t('connection.createConnection')}
          colorScheme='blue'
          icon={<AddIcon />}
          onClick={onCreateOpen}
        />
      </Flex>
      <Box>
        <ReactTable
          columns={columns}
          data={connections}
          isEmpty={isFetched && !connections.length}
          isLoading={isFetching || isConfirming}
        />
      </Box>

      <AddConnectionModal onClose={onCreateClose} isOpen={isCreateOpen} />
    </Flex>
  );
};

export default SyncSharepoint;
